import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CompanyApi } from '@api/companies.api';
import { AddressCoords, Company, PagedResult } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { getDistance } from 'geolib';
import { chain } from 'lodash';
import { debounceTime } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';

export function ValidateAutocomplete(): object {
  return { required: true };
}

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CompaniesComponent),
  multi: true
};

@Component({
  selector: 'companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class CompaniesComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() hint: boolean;
  @Input() floatLabel = true;
  @Input() preloadResults = false;
  @Input() appearance = 'outline';
  @Input() floatLabelText: string;
  @Input() compareWithCoords: AddressCoords = null;

  public companies: Array<Company> = [];
  public inputValue = new FormControl();
  public selectedCompany: Company;
  public loading = false;

  public options = {
    limit: 25,
    offset: 0,
    orderBy: 'name_asc'
  };

  constructor(
    private companiesApi: CompanyApi,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.inputValue.disable();
    }

    if (this.required) {
      this.inputValue.setValidators(ValidateAutocomplete);
    }

    this.inputValue.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.companies = [];

      if (value || this.preloadResults) {
        this.searchCompanies(value || '');
      }
    });
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }): void {
    for (const propertyName in changes) {
      if (propertyName === 'disabled') {
        this.disabled = changes[propertyName].currentValue;
        this.disabled ? this.inputValue.disable() : this.inputValue.enable();
      }
    }
  }

  onChange: (_: any) => void = () => { };
  onTouched: (_: any) => void = () => { };

  searchCompanies(searchTerm?: Record<string, any>): void {
    const query = { name_contains: searchTerm && searchTerm.name ? searchTerm.name : searchTerm };
    const options = {
      limit: this.options.limit,
      offset: this.options.offset,
      orderBy: this.options.orderBy,
      orderByDistance: this.compareWithCoords && {
        direction: 'asc',
        distance: {
          latitude: Number(this.compareWithCoords.latitude),
          longitude: Number(this.compareWithCoords.longitude)
        }
      }
    };

    this.loading = true;
    this.companiesApi.search(query, options).subscribe((result: PagedResult) => {
      this.loading = false;
      if (!this.compareWithCoords) {
        this.companies = result.items;
        return;
      }

      this.companies = chain(result.items)
        .map((item) => {
          item.distance = item.address && item.address.coords.latitude && item.address.coords.longitude
            ? getDistance(this.compareWithCoords, item.address.coords)
            : 0;

          item.name = this.displayWithDistance(item);
          return item;
        })
        .sortBy('distance', 'asc')
        .value();
    });
  }

  writeValue(companySelected: any): void {
    if (companySelected) {
      this.companiesApi.searchOne(companySelected.id).then((company) => {
        this.selectedCompany = company;
        this.inputValue.setErrors(null);
      });
    } else {
      this.deselectCompany();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputValue.disable();
    } else {
      this.inputValue.enable();
    }
  }

  setSelectedCompany(): void {
    if (this.inputValue.value) {
      this.selectedCompany = this.companies.find((company) => {
        return company.id === this.inputValue.value.id;
      });

      this.onChange(this.inputValue.value);
      this.inputValue.setErrors(null);
    }
  }

  deselectCompany(): void {
    this.onChange(null);
    this.selectedCompany = null;
    this.inputValue.setValue(null);
    this.inputValue.setErrors(null);
  }

  private displayWithDistance(value: Company): string {
    try {
      if (value.distance) {
        return `${value.name} - (${(value.distance / 1000).toFixed(2).replace('.', ',')} km)`;
      }

      return value.name;
    } catch {
      return null;
    }
  }
}
